<template>
  <div class="p-0 max-w-6xl">
    <div class="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-6">
      <h1 class="text-2xl font-bold text-gray-800">Notificaciones</h1>
      <span class="px-3 py-1 text-sm font-medium rounded-full bg-indigo-100 text-indigo-800">
        Solo administración (eventos, reglas y grupos)
      </span>
    </div>

    <p v-if="globalError" class="mb-4 p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg text-sm">
      {{ globalError }}
    </p>

    <!-- Tabs -->
    <div class="border-b border-gray-200 mb-6">
      <nav class="flex flex-wrap gap-1 -mb-px">
        <button
          v-for="t in tabs"
          :key="t.id"
          type="button"
          @click="activeTab = t.id"
          :class="[
            'px-4 py-2 text-sm font-medium border-b-2 transition-colors',
            activeTab === t.id
              ? 'border-indigo-600 text-indigo-600'
              : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300',
          ]"
        >
          {{ t.label }}
        </button>
      </nav>
    </div>

    <!-- Eventos -->
    <section v-show="activeTab === 'events'">
      <div class="flex justify-end mb-4">
        <button
          type="button"
          @click="openEventModal(null)"
          class="px-4 py-2 bg-indigo-600 text-white text-sm rounded-lg hover:bg-indigo-700"
        >
          Nuevo evento
        </button>
      </div>
      <div v-if="loadingEvents" class="text-gray-600">Cargando eventos…</div>
      <div v-else class="overflow-x-auto border border-gray-200 rounded-lg">
        <table class="min-w-full divide-y divide-gray-200">
          <thead class="bg-gray-50">
            <tr>
              <th class="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Clave</th>
              <th class="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Descripción</th>
              <th class="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase w-32">Acciones</th>
            </tr>
          </thead>
          <tbody class="bg-white divide-y divide-gray-200">
            <tr v-for="ev in events" :key="ev.id" class="hover:bg-gray-50">
              <td class="px-4 py-2 text-sm text-gray-900 font-mono">{{ ev.key }}</td>
              <td class="px-4 py-2 text-sm text-gray-700">{{ ev.description || '—' }}</td>
              <td class="px-4 py-2 text-right text-sm">
                <button type="button" class="text-indigo-600 hover:underline mr-2" @click="openEventModal(ev)">Editar</button>
                <button type="button" class="text-red-600 hover:underline" @click="confirmDeleteEvent(ev)">Eliminar</button>
              </td>
            </tr>
          </tbody>
        </table>
        <p v-if="!events.length" class="p-4 text-sm text-gray-500 text-center">No hay eventos.</p>
      </div>
    </section>

    <!-- Reglas -->
    <section v-show="activeTab === 'rules'">
      <div class="flex flex-col sm:flex-row sm:items-end gap-4 mb-4">
        <div>
          <label class="block text-sm text-gray-600 mb-1">Filtrar por evento</label>
          <select
            v-model="ruleEventFilter"
            @change="loadRules"
            class="border-gray-200 rounded-md text-sm w-full sm:w-72 focus:border-indigo-600 focus:ring-indigo-500"
          >
            <option value="">Todos</option>
            <option v-for="ev in events" :key="ev.id" :value="ev.id">
              {{ ev.key }}
            </option>
          </select>
        </div>
        <button
          type="button"
          @click="openRuleModal(null)"
          :disabled="!events.length"
          class="px-4 py-2 bg-indigo-600 text-white text-sm rounded-lg hover:bg-indigo-700 disabled:opacity-50"
        >
          Nueva regla
        </button>
      </div>
      <div v-if="loadingRules" class="text-gray-600">Cargando reglas…</div>
      <div v-else class="overflow-x-auto border border-gray-200 rounded-lg">
        <table class="min-w-full divide-y divide-gray-200 text-sm">
          <thead class="bg-gray-50">
            <tr>
              <th class="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Evento</th>
              <th class="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Enviar a</th>
              <th class="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">ID destino</th>
              <th class="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Canal</th>
              <th class="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Activa</th>
              <th class="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">Acciones</th>
            </tr>
          </thead>
          <tbody class="bg-white divide-y divide-gray-200">
            <tr v-for="r in rules" :key="r.id" class="hover:bg-gray-50">
              <td class="px-3 py-2 text-gray-900">{{ eventLabel(r.event_id) }}</td>
              <td class="px-3 py-2">{{ r.send_to_type }}</td>
              <td class="px-3 py-2 font-mono text-xs break-all max-w-xs">{{ r.send_to_id }}</td>
              <td class="px-3 py-2">{{ r.channel || '—' }}</td>
              <td class="px-3 py-2">{{ r.is_active === false ? 'No' : 'Sí' }}</td>
              <td class="px-3 py-2 text-right">
                <button type="button" class="text-indigo-600 hover:underline mr-2" @click="openRuleModal(r)">Editar</button>
                <button type="button" class="text-red-600 hover:underline" @click="confirmDeleteRule(r)">Eliminar</button>
              </td>
            </tr>
          </tbody>
        </table>
        <p v-if="!rules.length" class="p-4 text-sm text-gray-500 text-center">No hay reglas.</p>
      </div>
    </section>

    <!-- Grupos -->
    <section v-show="activeTab === 'groups'">
      <div class="flex justify-end mb-4">
        <button
          type="button"
          @click="openGroupModal(null)"
          class="px-4 py-2 bg-indigo-600 text-white text-sm rounded-lg hover:bg-indigo-700"
        >
          Nuevo grupo
        </button>
      </div>
      <div v-if="loadingGroups" class="text-gray-600">Cargando grupos…</div>
      <div v-else class="space-y-4">
        <div
          v-for="g in groups"
          :key="g.id"
          class="border border-gray-200 rounded-lg overflow-hidden"
        >
          <div
            class="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 px-4 py-3 bg-gray-50"
          >
            <div>
              <span class="font-medium text-gray-900">{{ g.name }}</span>
            </div>
            <div class="flex flex-wrap gap-2">
              <button type="button" class="text-sm text-indigo-600 hover:underline" @click="openGroupModal(g)">Renombrar</button>
              <button
                type="button"
                class="text-sm text-indigo-600 hover:underline"
                @click="toggleGroupUsers(g.id)"
              >
                {{ expandedGroupId === g.id ? 'Ocultar miembros' : 'Miembros' }}
              </button>
              <button type="button" class="text-sm text-red-600 hover:underline" @click="confirmDeleteGroup(g)">Eliminar</button>
            </div>
          </div>
          <div v-if="expandedGroupId === g.id" class="px-4 py-3 border-t border-gray-200 bg-white">
            <div v-if="loadingGroupUsers === g.id" class="text-sm text-gray-500">Cargando…</div>
            <div v-else>
              <div class="flex flex-col sm:flex-row gap-2 mb-3">
                <div class="relative flex-1 min-w-0">
                  <input
                    v-model="addUserSearchQuery[g.id]"
                    type="search"
                    autocomplete="off"
                    placeholder="Buscar por nombre o apellido…"
                    class="w-full border-gray-200 rounded-md text-sm focus:border-indigo-600 focus:ring-indigo-500"
                    @focus="onGroupAddUserFocus(g.id)"
                    @blur="onGroupAddUserBlur"
                    @input="onGroupAddUserInput(g.id)"
                  />
                  <ul
                    v-show="addUserSearchOpen[g.id] && (addUserSearchResults[g.id] || []).length"
                    class="absolute z-50 left-0 right-0 mt-1 max-h-52 overflow-y-auto border border-gray-200 rounded-md bg-white shadow-lg text-sm"
                  >
                    <li
                      v-for="u in addUserSearchResults[g.id] || []"
                      :key="userRowKey(u)"
                      class="px-3 py-2 cursor-pointer hover:bg-indigo-50 border-b border-gray-100 last:border-0"
                      @mousedown.prevent="selectUserForGroup(g.id, u)"
                    >
                      <span class="text-gray-900">{{ groupAddUserNameLine(u) }}</span>
                      <span
                        v-if="groupAddUserDniLine(u)"
                        class="block text-xs text-gray-500 mt-0.5"
                      >{{ groupAddUserDniLine(u) }}</span>
                    </li>
                  </ul>
                  <p v-if="addUserSearchLoading === g.id" class="mt-1 text-xs text-gray-500">Buscando…</p>
                </div>
                <div class="flex gap-2">
                  <input
                    v-model="addUserIdManual[g.id]"
                    type="text"
                    placeholder="o UUID de usuario"
                    class="flex-1 min-w-0 border-gray-200 rounded-md text-sm font-mono focus:border-indigo-600 focus:ring-indigo-500"
                  />
                  <button
                    type="button"
                    @click="addUserToGroup(g.id)"
                    class="px-3 py-1.5 bg-indigo-600 text-white text-sm rounded-md hover:bg-indigo-700 whitespace-nowrap"
                  >
                    Añadir
                  </button>
                </div>
              </div>
              <ul v-if="(groupUserRows[g.id] || []).length" class="divide-y divide-gray-100 text-sm">
                <li
                  v-for="row in groupUserRows[g.id] || []"
                  :key="row.userId"
                  class="py-1.5 flex justify-between items-center gap-2"
                >
                  <div class="min-w-0 flex-1">
                    <span class="text-sm text-gray-900">{{ row.nameLine }}</span>
                    <span
                      v-if="row.dniLine"
                      class="block text-xs text-gray-500 mt-0.5"
                    >{{ row.dniLine }}</span>
                  </div>
                  <button
                    type="button"
                    class="text-red-600 text-xs hover:underline shrink-0"
                    @click="removeUserFromGroup(g.id, row.userId)"
                  >
                    Quitar
                  </button>
                </li>
              </ul>
              <p v-else class="text-sm text-gray-500">No hay miembros en este grupo.</p>
            </div>
          </div>
        </div>
        <p v-if="!groups.length" class="text-sm text-gray-500">No hay grupos.</p>
      </div>
    </section>

    <!-- Modal evento -->
    <div
      v-if="eventModalOpen"
      class="fixed inset-0 z-40 flex items-center justify-center p-4 bg-black/50"
      @click.self="eventModalOpen = false"
    >
      <div class="bg-white rounded-lg shadow-xl max-w-md w-full p-6" @click.stop>
        <h3 class="text-lg font-semibold mb-4">{{ eventEditing ? 'Editar evento' : 'Nuevo evento' }}</h3>
        <div class="space-y-3">
          <div>
            <label class="block text-sm text-gray-600 mb-1">Clave (key) *</label>
            <input v-model="eventForm.key" type="text" class="w-full border-gray-200 rounded-md text-sm" />
          </div>
          <div>
            <label class="block text-sm text-gray-600 mb-1">Descripción</label>
            <textarea v-model="eventForm.description" rows="3" class="w-full border-gray-200 rounded-md text-sm" />
          </div>
        </div>
        <p v-if="formError" class="mt-2 text-sm text-red-600">{{ formError }}</p>
        <div class="mt-4 flex justify-end gap-2">
          <button type="button" class="px-3 py-1.5 text-sm text-gray-700" @click="eventModalOpen = false">Cancelar</button>
          <button
            type="button"
            class="px-4 py-1.5 text-sm bg-indigo-600 text-white rounded-md hover:bg-indigo-700"
            @click="saveEvent"
          >
            Guardar
          </button>
        </div>
      </div>
    </div>

    <!-- Modal regla -->
    <div
      v-if="ruleModalOpen"
      class="fixed inset-0 z-40 flex items-center justify-center p-4 bg-black/50"
      @click.self="ruleModalOpen = false"
    >
      <div class="bg-white rounded-lg shadow-xl max-w-lg w-full p-6 max-h-[90vh] overflow-y-auto" @click.stop>
        <h3 class="text-lg font-semibold mb-4">{{ ruleEditing ? 'Editar regla' : 'Nueva regla' }}</h3>
        <div class="space-y-3 text-sm">
          <div>
            <label class="block text-gray-600 mb-1">Evento *</label>
            <select v-model="ruleForm.event_id" class="w-full border-gray-200 rounded-md">
              <option v-for="ev in events" :key="ev.id" :value="ev.id">{{ ev.key }}</option>
            </select>
          </div>
          <div>
            <label class="block text-gray-600 mb-1">Tipo destino *</label>
            <select
              v-model="ruleForm.send_to_type"
              class="w-full border-gray-200 rounded-md"
              @change="onRuleSendToTypeChange"
            >
              <!-- <option value="user">user</option> -->
              <!-- <option value="role">role</option> -->
              <option value="group">group</option>
            </select>
          </div>
          <!-- Usuario: búsqueda + selección (apellido, nombre) -->
          <div v-if="ruleForm.send_to_type === 'user'">
            <label class="block text-gray-600 mb-1">Usuario *</label>
            <div class="relative">
              <input
                v-model="ruleUserSearch"
                type="search"
                autocomplete="off"
                placeholder="Buscar por apellido, nombre, email o ID…"
                class="w-full border-gray-200 rounded-md text-sm"
                @focus="ruleUserDropdownOpen = true"
                @blur="onRuleUserInputBlur"
                @input="ruleUserDropdownOpen = true"
              />
              <ul
                v-show="ruleUserDropdownOpen && filteredRuleUsers.length"
                class="absolute z-50 left-0 right-0 mt-1 max-h-52 overflow-y-auto border border-gray-200 rounded-md bg-white shadow-lg text-sm"
              >
                <li
                  v-for="u in filteredRuleUsers"
                  :key="userRowKey(u)"
                  class="px-3 py-2 cursor-pointer hover:bg-indigo-50 border-b border-gray-100 last:border-0"
                  @mousedown.prevent="selectRuleUser(u)"
                >
                  <span>{{ userLabel(u) }}</span>
                  <span class="block text-xs text-gray-400 font-mono truncate">{{ userRowId(u) }}</span>
                </li>
              </ul>
            </div>
            <p v-if="ruleForm.send_to_id" class="mt-1 text-xs text-gray-500 font-mono break-all">
              ID: {{ ruleForm.send_to_id }}
            </p>
            <p v-else class="mt-1 text-xs text-amber-700">Elegí un usuario de la lista de resultados.</p>
          </div>
          <!-- Grupo: lista de grupos de notificaciones -->
          <div v-else-if="ruleForm.send_to_type === 'group'">
            <label class="block text-gray-600 mb-1">Grupo *</label>
            <select v-model="ruleForm.send_to_id" class="w-full border-gray-200 rounded-md">
              <option value="">— Seleccionar grupo —</option>
              <option v-for="g in groups" :key="g.id" :value="g.id">
                {{ g.name }} ({{ g.id }})
              </option>
            </select>
            <p v-if="!groups.length" class="mt-1 text-xs text-amber-700">No hay grupos. Creá uno en la pestaña Grupos.</p>
          </div>
          <!-- Rol: roles del sistema -->
          <div v-else-if="ruleForm.send_to_type === 'role'">
            <label class="block text-gray-600 mb-1">Rol *</label>
            <select v-model="ruleForm.send_to_id" class="w-full border-gray-200 rounded-md">
              <option value="">— Seleccionar rol —</option>
              <option v-for="role in allRoles" :key="role.id" :value="role.id">
                {{ roleOptionLabel(role.name) }} ({{ role.name }})
              </option>
            </select>
            <p v-if="!allRoles.length" class="mt-1 text-xs text-amber-700">No se pudieron cargar los roles.</p>
          </div>
          <div>
            <label class="block text-gray-600 mb-1">Canal</label>
            <select v-model="ruleForm.channel" class="w-full border-gray-200 rounded-md">
              <option value="">(defecto)</option>
              <option value="app">app</option>
              <option value="email">email</option>
              <option value="whatsapp">whatsapp</option>
            </select>
          </div>
          <div class="flex items-center gap-2">
            <input id="rule-active" v-model="ruleForm.is_active" type="checkbox" class="rounded border-gray-300" />
            <label for="rule-active">Regla activa</label>
          </div>
        </div>
        <p v-if="formError" class="mt-2 text-sm text-red-600">{{ formError }}</p>
        <div class="mt-4 flex justify-end gap-2">
          <button type="button" class="px-3 py-1.5 text-sm text-gray-700" @click="ruleModalOpen = false">Cancelar</button>
          <button type="button" class="px-4 py-1.5 text-sm bg-indigo-600 text-white rounded-md hover:bg-indigo-700" @click="saveRule">
            Guardar
          </button>
        </div>
      </div>
    </div>

    <!-- Modal grupo -->
    <div
      v-if="groupModalOpen"
      class="fixed inset-0 z-40 flex items-center justify-center p-4 bg-black/50"
      @click.self="groupModalOpen = false"
    >
      <div class="bg-white rounded-lg shadow-xl max-w-md w-full p-6" @click.stop>
        <h3 class="text-lg font-semibold mb-4">{{ groupEditing ? 'Editar grupo' : 'Nuevo grupo' }}</h3>
        <div>
          <label class="block text-sm text-gray-600 mb-1">Nombre *</label>
          <input v-model="groupForm.name" type="text" class="w-full border-gray-200 rounded-md text-sm" />
        </div>
        <p v-if="formError" class="mt-2 text-sm text-red-600">{{ formError }}</p>
        <div class="mt-4 flex justify-end gap-2">
          <button type="button" class="px-3 py-1.5 text-sm text-gray-700" @click="groupModalOpen = false">Cancelar</button>
          <button type="button" class="px-4 py-1.5 text-sm bg-indigo-600 text-white rounded-md hover:bg-indigo-700" @click="saveGroup">
            Guardar
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import {
  notificationEventsApi,
  notificationRulesApi,
  notificationGroupsApi,
} from '@/service/notifications.service';

function unwrapList(res) {
  const d = res.data;
  if (Array.isArray(d)) return d;
  if (Array.isArray(d?.data)) return d.data;
  if (Array.isArray(d?.data?.data)) return d.data.data;
  return [];
}

/** IDs de miembros de grupo (misma forma de envoltura que otras respuestas). */
function unwrapUserIdList(d) {
  if (d == null) return [];
  if (Array.isArray(d)) return normalizeUserIdArray(d);
  if (Array.isArray(d.data)) return normalizeUserIdArray(d.data);
  if (Array.isArray(d.data?.data)) return normalizeUserIdArray(d.data.data);
  if (Array.isArray(d.user_ids)) return normalizeUserIdArray(d.user_ids);
  if (Array.isArray(d.data?.user_ids)) return normalizeUserIdArray(d.data.user_ids);
  if (Array.isArray(d.data?.data?.user_ids)) return normalizeUserIdArray(d.data.data.user_ids);
  return [];
}

function normalizeUserIdArray(arr) {
  if (!Array.isArray(arr) || !arr.length) return [];
  return arr
    .map((item) => {
      if (item == null) return null;
      if (typeof item === 'string' || typeof item === 'number') return String(item).trim();
      const id = item.user_id ?? item.userId ?? item.id;
      if (id != null && id !== '') return String(id).trim();
      return null;
    })
    .filter((id) => id);
}

/** Cuerpo de usuario en GET /user_profile (mismo anidado que en /users: res.data.data.data). */
function unwrapUserProfileRecord(res) {
  if (!res) return null;
  const raw = res.data?.data?.data ?? res.data?.data ?? res.data;
  if (raw == null) return null;
  if (Array.isArray(raw)) {
    return raw.length && typeof raw[0] === 'object' ? raw[0] : null;
  }
  if (typeof raw === 'object' && (raw.id || raw.Profile || raw.profile || raw.email || raw.User)) {
    return raw;
  }
  if (raw.data) return raw.data;
  return raw;
}

export default {
  name: 'NotificationsAdminView',

  data() {
    return {
      activeTab: 'events',
      tabs: [
        { id: 'events', label: 'Eventos' },
        { id: 'rules', label: 'Reglas' },
        { id: 'groups', label: 'Grupos' },
      ],
      globalError: null,
      loadingEvents: false,
      loadingRules: false,
      loadingGroups: false,
      loadingGroupUsers: null,
      events: [],
      rules: [],
      groups: [],
      ruleEventFilter: '',
      expandedGroupId: null,
      groupUserIds: {},
      /** { [groupId]: { userId, nameLine, dniLine }[] } filas alineadas con los miembros */
      groupUserRows: {},
      /** caché opcional al resolver nombres para no repetir /user_profile */
      userDisplayById: {},
      addUserByGroup: {},
      addUserIdManual: {},
      addUserSearchQuery: {},
      addUserSearchResults: {},
      addUserSearchOpen: {},
      addUserSearchLoading: null,
      groupUserSearchSeq: {},
      allUsers: [],
      allRoles: [],
      ruleUserSearch: '',
      ruleUserDropdownOpen: false,
      eventModalOpen: false,
      eventEditing: null,
      eventForm: { key: '', description: '' },
      ruleModalOpen: false,
      ruleEditing: null,
      ruleForm: {
        event_id: '',
        send_to_type: 'group',
        send_to_id: '',
        channel: '',
        is_active: true,
      },
      groupModalOpen: false,
      groupEditing: null,
      groupForm: { name: '' },
      formError: null,
    };
  },

  computed: {
    ...mapGetters('authentication', ['currentUser']),
    filteredRuleUsers() {
      const list = Array.isArray(this.allUsers) ? this.allUsers : [];
      const q = (this.ruleUserSearch || '').trim().toLowerCase();
      const withMeta = list.map((u) => ({ u, label: this.userLabel(u) }));
      withMeta.sort((a, b) => a.label.localeCompare(b.label, 'es', { sensitivity: 'base' }));
      if (!q) {
        return withMeta.slice(0, 60).map((x) => x.u);
      }
      return withMeta
        .filter(({ u, label }) => {
          const id = (this.userRowId(u) || '').toLowerCase();
          const em = (u.email || '').toLowerCase();
          const ln = (u.Profile?.lastname || '').toLowerCase();
          const fn = (u.Profile?.firstname || '').toLowerCase();
          return (
            label.toLowerCase().includes(q) ||
            id.includes(q) ||
            em.includes(q) ||
            ln.includes(q) ||
            fn.includes(q)
          );
        })
        .slice(0, 100)
        .map((x) => x.u);
    },
  },

  watch: {
    allUsers() {
      if (!this.ruleModalOpen || !this.ruleEditing || this.ruleForm.send_to_type !== 'user' || !this.ruleForm.send_to_id) {
        return;
      }
      if (this.ruleUserSearch) return;
      const u = this.allUsers.find((x) => this.userRowId(x) === String(this.ruleForm.send_to_id));
      if (u) this.ruleUserSearch = this.userLabel(u);
    },
  },

  created() {
    if (this.currentUser?.role !== 'admin') {
      this.$router.replace({ name: 'home' });
      return;
    }
    this.bootstrap();
  },

  methods: {
    async bootstrap() {
      this.globalError = null;
      await this.loadEvents();
      await this.loadRules();
      await this.loadGroups();
      this.loadAllUsers();
      this.loadAllRoles();
    },

    eventLabel(eventId) {
      const ev = this.events.find((e) => e.id === eventId);
      return ev ? ev.key : eventId;
    },

    loadAllUsers() {
      this.$http
        .get('/users')
        .then((res) => {
          const raw = res.data?.data?.data || res.data?.data || res.data;
          this.allUsers = Array.isArray(raw) ? raw : [];
        })
        .catch(() => {
          this.allUsers = [];
        });
    },

    async loadAllRoles() {
      try {
        const res = await this.$http.get('/roles');
        const raw = res.data?.data ?? res.data;
        let list = Array.isArray(raw) ? raw : [];
        
        const filtered = ['admin', 'teacher', 'student']
        
        list =  list.filter(el => !filtered.includes(el.name))

        this.allRoles = [...list].sort((a, b) =>
          (a.name || '').localeCompare(b.name || '', 'es', { sensitivity: 'base' })
        );
      } catch {
        this.allRoles = [];
      }
    },

    userRowId(entry) {
      if (!entry) return '';
      const u = entry.User?.id ?? entry.user_id ?? entry.id;
      return u != null && u !== '' ? String(u) : '';
    },

    userRowKey(entry) {
      return this.userRowId(entry) || String(entry?.id ?? '');
    },

    userLabel(entry) {
      if (!entry) return '';
      const p = entry.Profile || entry.profile;
      if (p && (p.lastname || p.firstname)) {
        return `${p.lastname || ''}, ${p.firstname || ''}`.trim();
      }
      if (entry.firstname != null || entry.lastname != null) {
        const s = this.groupAddUserNameLine(entry);
        return s || this.userRowId(entry) || '—';
      }
      return entry.name || entry.email || this.userRowId(entry) || '—';
    },

    /** Apellido y nombre (respuesta /users/search y perfiles) — sin mostrar user_id. */
    groupAddUserNameLine(entry) {
      if (!entry) return '';
      const ln = entry.Profile?.lastname ?? entry.profile?.lastname ?? entry.lastname;
      const fn = entry.Profile?.firstname ?? entry.profile?.firstname ?? entry.firstname;
      const a = (ln != null && String(ln).trim() !== '') ? String(ln).trim() : '';
      const b = (fn != null && String(fn).trim() !== '') ? String(fn).trim() : '';
      if (!a && !b) return '';
      if (a && b) return `${a}, ${b}`;
      return a || b;
    },

    groupAddUserDniLine(entry) {
      if (!entry) return '';
      const raw = entry.dni ?? entry.Profile?.dni ?? entry.profile?.dni;
      if (raw == null || String(raw).trim() === '') return '';
      return `DNI ${String(raw).trim()}`;
    },

    groupMemberNameFromEntry(entry) {
      if (!entry) return '—';
      const byParts = this.groupAddUserNameLine(entry);
      if (byParts) return byParts;
      if (entry.name && String(entry.name).trim()) return String(entry.name).trim();
      if (entry.email && String(entry.email).trim()) return String(entry.email).trim();
      return '—';
    },

    async buildGroupMemberRow(uid) {
      const id = String(uid).trim();
      if (!id) {
        return { userId: id, nameLine: '—', dniLine: '' };
      }
      if (Object.prototype.hasOwnProperty.call(this.userDisplayById, id)) {
        const c = this.userDisplayById[id];
        return { userId: id, nameLine: c.nameLine || '—', dniLine: c.dniLine || '' };
      }
      const uEntry = (this.allUsers || []).find((u) => this.userRowId(u) === id);
      if (uEntry) {
        const nameLine = this.groupMemberNameFromEntry(uEntry);
        const dniLine = this.groupAddUserDniLine(uEntry);
        this.userDisplayById[id] = { nameLine, dniLine };
        return { userId: id, nameLine, dniLine };
      }
      try {
        const res = await this.$http.get(`/user_profile/${id}`);
        const row = unwrapUserProfileRecord(res);
        const nameLine = this.groupMemberNameFromEntry(row);
        const dniLine = this.groupAddUserDniLine(row);
        this.userDisplayById[id] = { nameLine, dniLine };
        return { userId: id, nameLine, dniLine };
      } catch {
        const fall = { nameLine: '—', dniLine: '' };
        this.userDisplayById[id] = fall;
        return { userId: id, ...fall };
      }
    },

    async buildGroupMemberRows(ids) {
      if (!Array.isArray(ids) || !ids.length) return [];
      return Promise.all((ids || []).map((u) => this.buildGroupMemberRow(u)));
    },

    selectRuleUser(u) {
      const id = this.userRowId(u);
      if (!id) return;
      this.ruleForm.send_to_id = id;
      this.ruleUserSearch = this.userLabel(u);
      this.ruleUserDropdownOpen = false;
    },

    onRuleUserInputBlur() {
      window.setTimeout(() => {
        this.ruleUserDropdownOpen = false;
      }, 200);
    },

    onRuleSendToTypeChange() {
      this.ruleForm.send_to_id = '';
      this.ruleUserSearch = '';
    },

    roleOptionLabel(name) {
      const n = (name || '').toLowerCase();
      const map = {
        admin: 'Administrador',
        administrative: 'Administrativo',
        executive: 'Ejecutivo',
        teacher: 'Docente',
        rti: 'RTI',
        director: 'Director',
        janitor: 'Personal de limpieza',
        student_assistant: 'Preceptor/a',
      };
      return map[n] || name || '—';
    },

    async loadEvents() {
      this.loadingEvents = true;
      this.globalError = null;
      try {
        const res = await notificationEventsApi.list();
        this.events = unwrapList(res);
      } catch (e) {
        this.globalError = e.response?.data?.message || e.message || 'Error al cargar eventos';
        this.events = [];
      } finally {
        this.loadingEvents = false;
      }
    },

    async loadRules() {
      this.loadingRules = true;
      this.globalError = null;
      const params = {};
      if (this.ruleEventFilter) params.event_id = this.ruleEventFilter;
      try {
        const res = await notificationRulesApi.list(params);
        this.rules = unwrapList(res);
      } catch (e) {
        this.globalError = e.response?.data?.message || e.message || 'Error al cargar reglas';
        this.rules = [];
      } finally {
        this.loadingRules = false;
      }
    },

    async loadGroups() {
      this.loadingGroups = true;
      this.globalError = null;
      try {
        const res = await notificationGroupsApi.list();
        this.groups = unwrapList(res);
      } catch (e) {
        this.globalError = e.response?.data?.message || e.message || 'Error al cargar grupos';
        this.groups = [];
      } finally {
        this.loadingGroups = false;
      }
    },

    openEventModal(row) {
      this.formError = null;
      this.eventEditing = row;
      this.eventForm = row
        ? { key: row.key || '', description: row.description || '' }
        : { key: '', description: '' };
      this.eventModalOpen = true;
    },

    async saveEvent() {
      this.formError = null;
      if (!this.eventForm.key?.trim()) {
        this.formError = 'La clave es obligatoria';
        return;
      }
      const body = {
        key: this.eventForm.key.trim(),
        description: this.eventForm.description?.trim() || undefined,
      };
      try {
        if (this.eventEditing) {
          await notificationEventsApi.update(this.eventEditing.id, body);
        } else {
          await notificationEventsApi.create(body);
        }
        this.eventModalOpen = false;
        await this.loadEvents();
        await this.loadRules();
      } catch (e) {
        this.formError = e.response?.data?.message || e.message || 'Error al guardar';
      }
    },

    confirmDeleteEvent(ev) {
      if (!window.confirm(`¿Eliminar el evento «${ev.key}»?`)) return;
      notificationEventsApi
        .remove(ev.id)
        .then(() => this.loadEvents().then(() => this.loadRules()))
        .catch((e) => {
          this.globalError = e.response?.data?.message || e.message;
        });
    },

    openRuleModal(row) {
      this.formError = null;
      this.ruleEditing = row;
      this.ruleUserSearch = '';
      this.ruleUserDropdownOpen = false;
      if (row) {
        this.ruleForm = {
          event_id: row.event_id,
          send_to_type: row.send_to_type || 'user',
          send_to_id: row.send_to_id || '',
          channel: row.channel || '',
          is_active: row.is_active !== false,
        };
        if ((row.send_to_type || 'user') === 'user' && row.send_to_id) {
          const u = this.allUsers.find((x) => this.userRowId(x) === String(row.send_to_id));
          this.ruleUserSearch = u ? this.userLabel(u) : '';
        }
      } else {
        this.ruleForm = {
          event_id: this.events[0]?.id || '',
          send_to_type: 'user',
          send_to_id: '',
          channel: '',
          is_active: true,
        };
      }
      this.ruleModalOpen = true;
    },

    async saveRule() {
      this.formError = null;
      if (!this.ruleForm.event_id || !this.ruleForm.send_to_id?.trim()) {
        this.formError = 'Completá evento e ID de destino';
        return;
      }
      const body = {
        event_id: this.ruleForm.event_id,
        send_to_type: this.ruleForm.send_to_type,
        send_to_id: this.ruleForm.send_to_id.trim(),
        is_active: this.ruleForm.is_active,
      };
      if (this.ruleForm.channel) body.channel = this.ruleForm.channel;
      try {
        if (this.ruleEditing) {
          await notificationRulesApi.update(this.ruleEditing.id, body);
        } else {
          await notificationRulesApi.create(body);
        }
        this.ruleModalOpen = false;
        await this.loadRules();
      } catch (e) {
        const msg = e.response?.data?.message || e.message;
        this.formError = e.response?.status === 409 ? (msg || 'Regla duplicada') : msg || 'Error al guardar';
      }
    },

    confirmDeleteRule(r) {
      if (!window.confirm('¿Eliminar esta regla?')) return;
      notificationRulesApi
        .remove(r.id)
        .then(() => this.loadRules())
        .catch((e) => {
          this.globalError = e.response?.data?.message || e.message;
        });
    },

    openGroupModal(row) {
      this.formError = null;
      this.groupEditing = row;
      this.groupForm = row ? { name: row.name || '' } : { name: '' };
      this.groupModalOpen = true;
    },

    async saveGroup() {
      this.formError = null;
      if (!this.groupForm.name?.trim()) {
        this.formError = 'El nombre es obligatorio';
        return;
      }
      const body = { name: this.groupForm.name.trim() };
      try {
        if (this.groupEditing) {
          await notificationGroupsApi.update(this.groupEditing.id, body);
        } else {
          await notificationGroupsApi.create(body);
        }
        this.groupModalOpen = false;
        await this.loadGroups();
      } catch (e) {
        this.formError = e.response?.data?.message || e.message || 'Error al guardar';
      }
    },

    confirmDeleteGroup(g) {
      if (!window.confirm(`¿Eliminar el grupo «${g.name}»?`)) return;
      notificationGroupsApi
        .remove(g.id)
        .then(() => {
          if (this.expandedGroupId === g.id) this.expandedGroupId = null;
          return this.loadGroups();
        })
        .catch((e) => {
          this.globalError = e.response?.data?.message || e.message;
        });
    },

    async toggleGroupUsers(groupId) {
      if (this.expandedGroupId === groupId) {
        this.expandedGroupId = null;
        return;
      }
      this.expandedGroupId = groupId;
      this.addUserIdManual = { ...this.addUserIdManual, [groupId]: '' };
      this.addUserByGroup = { ...this.addUserByGroup, [groupId]: '' };
      this.addUserSearchQuery = { ...this.addUserSearchQuery, [groupId]: '' };
      this.addUserSearchResults = { ...this.addUserSearchResults, [groupId]: [] };
      this.addUserSearchOpen = { ...this.addUserSearchOpen, [groupId]: false };
      await this.fetchGroupUsers(groupId);
    },

    onGroupAddUserFocus(groupId) {
      const list = this.addUserSearchResults[groupId] || [];
      if (list.length) this.addUserSearchOpen = { ...this.addUserSearchOpen, [groupId]: true };
    },

    onGroupAddUserBlur() {
      window.setTimeout(() => {
        const id = this.expandedGroupId;
        if (id) this.addUserSearchOpen = { ...this.addUserSearchOpen, [id]: false };
      }, 200);
    },

    onGroupAddUserInput(groupId) {
      this.addUserByGroup = { ...this.addUserByGroup, [groupId]: '' };
      const q = (this.addUserSearchQuery[groupId] || '').trim();
      this.addUserSearchOpen = { ...this.addUserSearchOpen, [groupId]: true };
      if (this._groupUserSearchTimer) {
        clearTimeout(this._groupUserSearchTimer);
        this._groupUserSearchTimer = null;
      }
      if (q.length < 2) {
        this.addUserSearchResults = { ...this.addUserSearchResults, [groupId]: [] };
        return;
      }
      this._groupUserSearchTimer = window.setTimeout(() => {
        this._groupUserSearchTimer = null;
        this.runGroupUserSearch(groupId, q);
      }, 300);
    },

    async runGroupUserSearch(groupId, q) {
      const seq = (this.groupUserSearchSeq[groupId] || 0) + 1;
      this.groupUserSearchSeq = { ...this.groupUserSearchSeq, [groupId]: seq };
      this.addUserSearchLoading = groupId;
      try {
        const res = await this.$http.get('/users/search', { params: { q } });
        if (this.groupUserSearchSeq[groupId] !== seq) return;
        const list = unwrapList(res);
        this.addUserSearchResults = { ...this.addUserSearchResults, [groupId]: list };
      } catch {
        if (this.groupUserSearchSeq[groupId] === seq) {
          this.addUserSearchResults = { ...this.addUserSearchResults, [groupId]: [] };
        }
      } finally {
        if (this.groupUserSearchSeq[groupId] === seq) {
          this.addUserSearchLoading = null;
        }
      }
    },

    selectUserForGroup(groupId, u) {
      const id = this.userRowId(u);
      if (!id) return;
      this.addUserByGroup = { ...this.addUserByGroup, [groupId]: id };
      this.addUserSearchQuery = { ...this.addUserSearchQuery, [groupId]: this.userLabel(u) };
      this.addUserSearchOpen = { ...this.addUserSearchOpen, [groupId]: false };
      this.addUserSearchResults = { ...this.addUserSearchResults, [groupId]: [] };
    },

    async fetchGroupUsers(groupId) {
      this.loadingGroupUsers = groupId;
      this.globalError = null;
      try {
        const res = await notificationGroupsApi.listUsers(groupId);
        const ids = unwrapUserIdList(res.data);
        this.groupUserIds = { ...this.groupUserIds, [groupId]: ids };
        (ids || []).forEach((u) => {
          const k = String(u).trim();
          if (k) delete this.userDisplayById[k];
        });
        let rows = [];
        try {
          rows = await this.buildGroupMemberRows(ids);
        } catch {
          rows = (ids || []).map((u) => ({
            userId: String(u).trim(),
            nameLine: '—',
            dniLine: '',
          }));
        }
        this.groupUserRows[groupId] = rows;
      } catch (e) {
        this.globalError = e.response?.data?.message || e.message;
        this.groupUserIds = { ...this.groupUserIds, [groupId]: [] };
        this.groupUserRows[groupId] = [];
      } finally {
        this.loadingGroupUsers = null;
      }
    },

    addUserToGroup(groupId) {
      const fromSelect = this.addUserByGroup[groupId];
      const manual = (this.addUserIdManual[groupId] || '').trim();
      const userId = manual || fromSelect;
      if (!userId) {
        this.globalError = 'Elegí un usuario o ingresá un UUID';
        return;
      }
      this.globalError = null;
      notificationGroupsApi
        .addUser(groupId, userId)
        .then(() => {
          this.addUserIdManual = { ...this.addUserIdManual, [groupId]: '' };
          this.addUserByGroup = { ...this.addUserByGroup, [groupId]: '' };
          this.addUserSearchQuery = { ...this.addUserSearchQuery, [groupId]: '' };
          this.addUserSearchResults = { ...this.addUserSearchResults, [groupId]: [] };
          return this.fetchGroupUsers(groupId);
        })
        .catch((e) => {
          this.globalError = e.response?.data?.message || e.message;
        });
    },

    removeUserFromGroup(groupId, userId) {
      if (!window.confirm('¿Quitar este usuario del grupo?')) return;
      notificationGroupsApi
        .removeUser(groupId, userId)
        .then(() => this.fetchGroupUsers(groupId))
        .catch((e) => {
          this.globalError = e.response?.data?.message || e.message;
        });
    },
  },
};
</script>
